<template>
  <main class="app-get-step" id="mainContent" ref="mainContentRef">
    <div class="app-main-content">
      <div class="insuranceShowError app-alert-error app-system-alert" v-if="insuranceShowErrorMessage">
        <p>To continue with your registration transaction you must have liability insurance and certify that you have read the Insurance statement.
          Please review your response below.
        </p>
      </div>
      <h1>{{ screenData.title }}</h1>

      <div class="app-content-panels">
        <!-- left panel (form) -->
        <div class="app-panel-left">
          <div>
            <p>By checking "Yes" below, I hereby make application to register the described vehicle, and as part of my application declare that I am the owner 
              or an authorized agent and certify under penalty of perjury that this vehicle is covered by liability insurance as described in 
              <a href="#" @click.prevent="showModalActive=true" tabindex="0"><strong>RIGL 31-47 Motor Vehicles Reparations Act</strong></a>  
              and will abide by conditions stated therein
            </p>
            <div class="app-input-group">
              <div class="app-input-selector">
                <input type="radio" id="yes" name="notify" v-model="insuranceInfo" value="yes" @change="insuranceError=false; insuranceShowErrorMessage=false">
                <label for="yes">Yes</label>  
              </div>
              <div class="app-input-selector">
                <input type="radio" id="no" name="notify-no" v-model="insuranceInfo" value="no" @change="insuranceError=false"> 
                <label for="no">No</label> 
              </div>
            </div>
          </div>
          <p class="app-error-message" v-if="insuranceError"> Response to this question is required. </p>
          <p id="app-error-message" class="sr-only" v-if="insuranceError"> Response to this question is required. </p>
          <BaseButtonGroup>
            <CustomButton @click="handleRouting(true)" class="resend-code">Previous</CustomButton>
            <CustomButton @click="handleRouting(false)">Next</CustomButton>
            <CustomButton v-if="!disableBtn" @click="getGoToProfile()" class="resend-code">Cancel and go to profile</CustomButton>
            <CustomButton :class="{ 'disabled': !disableBtn }" :aria-disabled="!disableBtn ? null: 'true'" @click="goToTxnCart(true)" 
              v-if="disableBtn" class="resend-code">Cancel and go to cart
            </CustomButton>
          </BaseButtonGroup>
        </div>
      </div>
    </div>
    <BaseModal modal-id="modalRules" :show-modal="showModalActive">
      <template #heading>
        <div>
          <span>Read this insurance statement then follow the instructions for the transaction:</span>           
        </div>
      </template>
      <template #content>
        <div class="app-modal-basic">
          <div>
            <p> The undersigned (hereinafter referred to as "applicant") swears that, in compliance with Title 31, Chapter 47 of the General Laws, Motor and Other Vehicles, known as the Motor Vehicles Reparations Act, he/she will not operate or allow to be operated the motor vehicle described in this registration nor any other motor vehicle unless all such motor vehicles are covered for financial security.</p>
            <p>Because of a concern over the rising toll of motor vehicle accidents and the suffering and loss thereby inflicted, the legislature determined that it is a matter of grave concern that motorists shall be financially able to respond in damages for their negligent acts so that innocent victims of motor vehicle accidents may be recompensated for the injury and financial loss inflicted upon them. The aforementioned act was passed to address such concern.</p>
            <p>The act requires every natural person, firm, partnership, association or corporation registering a vehicle or renewing the registration of a vehicle to aver that he/she will provide financial security on same.</p>
            <p>The obligation will be met by maintaining a policy of liability insurance with bodily injury limits of $25,000 to any one person and $50,000 to two or more persons in any one accident along with a limit of $25,000 for injury to or destruction of property of others in any one accident or a combined bodily injury and property damage liability limit of $75,000; 
              OR by filing with the assistant director for motor vehicles in the Department of Transportation, a financial security bond in the amount of $75,000; OR by making a financial security deposit with the assistant director of motor vehicles in the Department of Transportation in the amount of $75,000; OR by qualifying as a self-insurer.</p>
            <p>Penalties for failure to comply with the provisions of the act are a minimum fine of $500., maximum fine of $5,000, and revocation of motor vehicle registration and license plates for a period of three months for the first offense, six months for the second and one year for the third and subsequent offenses. In addition, violation of the law a third or subsequent time is deemed a misdemeanor and punishment may include a maximum fine of $5,000., a year imprisonment or both.</p>
            <p>The existence of this act and its requirements does not prevent the possibility that the applicant may be involved in an accident with an owner or operator of a vehicle who is without financial responsibility.</p>
          </div>
        </div>
      </template>
      <template #footing>
        <BaseButtonGroup> 
          <template #default>
            <button @click.prevent="getShowModalActive()">
              Close
            </button> 
          </template>
        </BaseButtonGroup>
      </template>
    </BaseModal>
  </main>
</template>


<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import BaseButtonGroup from '@/components/ui/BaseButtonGroup';
import BaseModal from '@/components/ui/BaseModal'; 
import * as COMMONS from '../../store/_commons';
import CustomButton from '../../components/CustomButton.vue';

// Vuex, Vue Router, and emits
const store = useStore();
const router = useRouter();
const emit = defineEmits(['getStep']);

// Reactive state data
const stepNum = ref(2);
const screenData = ref({});
const showModalActive = ref(false);
const insuranceInfo = ref('');
const insuranceShowErrorMessage = ref(false);
const insuranceError = ref(false);
const mainContentRef = ref(null);

// Computed properties
const STEPS = computed(() => store.state.STEPS);
const isBusinessUser = computed(() => store.state.guestLogin.isBusinessUser);
const profileRouteName = computed(() => {
  return isBusinessUser.value ? 'BusinessProfile' : 'Profile';
});

const disableBtn = computed(() => {
    return store.state.guestLogin.cartTransactions?.cartTransactions?.length > 1;
});

// Vuex actions 
const getSaveWipData = () => store.dispatch(COMMONS.GUEST_SAVEWIPDATA);
const getCancelTransaction = () => store.dispatch(COMMONS.GUEST_CANCELTRANSACTION);

// Methods
const handleRouting = async (isPrev = false) => {
  if (isPrev) {
    await getCancelTransaction();
    emit('getStep', 1);
    store.state.guestLogin.step = 1;
  } 
  else if (insuranceInfo.value === 'yes') {
    await getSaveWipData();
  } else if (insuranceInfo.value === 'no') {
    insuranceShowErrorMessage.value = true;
  } else {
    insuranceError.value = true;
  }
};

const goToTxnCart = async () => {
  await getCancelTransaction();
  emit('getStep', 4);
  store.state.guestLogin.step = 4;
};

const getGoToProfile = async () => {
  await getCancelTransaction();
  router.push({ name: profileRouteName.value });
};

const getShowModalActive = async () => {
  showModalActive.value = false;
  await nextTick(); 

  if (mainContentRef.value) {
    mainContentRef.value.setAttribute("tabindex", "1");
    mainContentRef.value.focus();
    mainContentRef.value.removeAttribute("tabindex");
    mainContentRef.value.scrollTop = 0;
  }
};

// eslint-disable-next-line no-unused-vars
const showRenewRegModal = async (val) => {
  if (val === 'Yes') {
    await getCancelTransaction();
    store.state.guestLogin.step = 4;
  }
};

// Lifecycle hooks
onMounted(async () => {
  screenData.value = STEPS.value[stepNum.value];
  window.scroll(0, 0);

  await nextTick();
  if (mainContentRef.value) {
    mainContentRef.value.focus();
  }
});
</script>


<style scoped lang="scss">
.app-alert-error {
  display: block;
  margin-top: 20px;
  margin-bottom: 20px;
  font-size: 18px;
  line-height: 28px;
  padding-left: 10px;
  padding-right: 10px;
}
.app-input-group {
  display: flex;
  margin-top: 0.5rem;
  gap: 2rem;
  .input-radio {
    min-width: 100px;
    input[type="radio"] {
    margin: 0.5em 0;
    }
  }
  label {
    margin-top: 0.65em;
  }
}
.app-modal-basic {
  overflow-y: auto;
  height: 300px;
}
.app-error-message {
  color: var(--ri-red);
  @extend %font-sans-regular;
  line-height: 1.35em;
  margin: unset;
  padding: 0.5em 0;
  &.--checker {
      margin-left: 1.5em;
  }
} 
.insuranceShowError >>> p {
  font-size: 1rem;
  line-height: 1.5em;
  padding-top: .5em;
  padding-bottom: .5em;
} 
.resend-code{
  background-color: white;
  color: var(--ri-blue);
  &:hover{
    background-color: var(--ri-lighter-blue);
    color: var(--ri-darker-blue);
  }
}
button {
  margin-bottom: 1.125em;
  &:not(:last-child) {
  margin-right: 1em;
  }
  &.disabled {
  cursor: not-allowed;
  opacity: 0.7;
  }
}
</style>
